import React from 'react';
import { CreditCard, TrendingUp, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const CreditsPage = () => {
  // Mock data (placeholder)
  const creditsData = {
    remaining: 50,
    total: 100,
    usedThisMonth: 35,
  };

  const usageHistory = [
    { date: '2025-01-19', description: 'Q4 Sales Analysis', credits: 5 },
    { date: '2025-01-18', description: 'Customer Churn Query', credits: 3 },
    { date: '2025-01-17', description: 'Marketing ROI Dashboard', credits: 8 },
    { date: '2025-01-16', description: 'Data Connection Setup', credits: 2 },
    { date: '2025-01-15', description: 'Revenue Forecasting', credits: 7 },
  ];

  const percentUsed = ((creditsData.total - creditsData.remaining) / creditsData.total) * 100;

  return (
    <div className="h-full bg-slate-900 flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-800/50">
        <h1 className="text-xl font-semibold text-white">Credits</h1>
        <p className="text-sm text-gray-400">Manage your credits and view usage history</p>
      </div>

      {/* Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-4xl space-y-6">
          {/* Credits Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Remaining Credits */}
            <div className="bg-slate-800/40 border border-slate-700/30 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-blue-400" />
                </div>
                <span className="text-gray-400 text-sm">Remaining</span>
              </div>
              <p className="text-3xl font-bold text-white">{creditsData.remaining}</p>
              <p className="text-xs text-gray-500 mt-1">of {creditsData.total} total</p>
            </div>

            {/* Used This Month */}
            <div className="bg-slate-800/40 border border-slate-700/30 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-purple-400" />
                </div>
                <span className="text-gray-400 text-sm">Used This Month</span>
              </div>
              <p className="text-3xl font-bold text-white">{creditsData.usedThisMonth}</p>
              <p className="text-xs text-gray-500 mt-1">credits consumed</p>
            </div>

            {/* Usage Bar */}
            <div className="bg-slate-800/40 border border-slate-700/30 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-amber-400" />
                </div>
                <span className="text-gray-400 text-sm">Usage</span>
              </div>
              <div className="w-full bg-slate-700/50 rounded-full h-3 mb-2">
                <div 
                  className={`h-3 rounded-full ${percentUsed > 80 ? 'bg-red-500' : percentUsed > 50 ? 'bg-amber-500' : 'bg-green-500'}`}
                  style={{ width: `${percentUsed}%` }}
                />
              </div>
              <p className="text-xs text-gray-500">{percentUsed.toFixed(0)}% used</p>
            </div>
          </div>

          {/* Buy More Credits */}
          <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-xl p-5 flex items-center justify-between">
            <div>
              <h3 className="text-white font-medium mb-1">Need more credits?</h3>
              <p className="text-gray-400 text-sm">Purchase additional credits to continue using Intugle</p>
            </div>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
              Buy Credits
            </Button>
          </div>

          {/* Usage History */}
          <div className="bg-slate-800/40 border border-slate-700/30 rounded-xl">
            <div className="px-5 py-4 border-b border-slate-700/30">
              <h3 className="text-white font-medium">Usage History</h3>
            </div>
            <div className="divide-y divide-slate-700/30">
              {usageHistory.map((item, index) => (
                <div key={index} className="px-5 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-white text-sm">{item.description}</p>
                    <p className="text-gray-500 text-xs">{item.date}</p>
                  </div>
                  <span className="text-gray-400 text-sm">-{item.credits} credits</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditsPage;
